/**
 * scheduledApi.js — API functions for the Scheduled Reports page.
 *
 * fetchSchedules              — load all schedules for a campus
 * fetchScheduleFilterOptions  — load all filter option datasets
 * fetchFrequencies            — load frequency options (Daily, Weekly, Monthly)
 * editSchedule                — update a schedule
 * deleteSchedules             — delete one or more schedules
 * sendScheduleReport          — trigger immediate send of a schedule
 */

import { apiFetch, apiTransaction } from '../../../../services/api.js';
import { getAuthHeaders } from '../../../../services/auth';

const getBaseUrl = () =>
    import.meta.env.VITE_DEVMODE === 'true'
        ? import.meta.env.VITE_DEVAPI
        : import.meta.env.VITE_API;

const getHeaders = () => getAuthHeaders();

// ---------------------------------------------------------------------------
// Fetch Schedules
// ---------------------------------------------------------------------------

/**
 * @param {number} campusId
 * @returns {Promise<Array>}
 */
export async function fetchSchedules(campusId) {
    return apiFetch({
        procedure: 'getreportschedules',
        reqType: 'stored',
        pKey: 'campus_id',
        pVal: campusId,
    });
}

// ---------------------------------------------------------------------------
// Fetch Frequencies
// ---------------------------------------------------------------------------

/**
 * @returns {Promise<Array>} — [{ window_frequency_id, frequency_name }]
 */
export async function fetchFrequencies() {
    return apiFetch({
        table: 'att_window_frequency',
        procedure: 'fetchall',
        reqType: 'direct',
    });
}

// ---------------------------------------------------------------------------
// Fetch Filter Options (full set for all report types)
// ---------------------------------------------------------------------------


// ---------------------------------------------------------------------------
// Edit Schedule
// ---------------------------------------------------------------------------

/**
 * @param {object} payload — new schedule data
 * @param {object} oldPayload — original schedule data (for audit)
 * @param {object} session — { campusId, userId, clientId }
 * @returns {Promise<[boolean, string?]>}
 */
export async function editSchedule(payload, oldPayload, session) {
    return apiTransaction({
        transType: 'schedule_edit',
        payload: [payload],
        oldPayload: oldPayload ? [oldPayload] : undefined,
        campusId: session.campusId,
        userId: session.userId,
        clientId: session.clientId,
    });
}

// ---------------------------------------------------------------------------
// Create Schedule (new)
// ---------------------------------------------------------------------------

/**
 * @param {object} payload — full schedule_add payload
 * @param {object} session — { campusId, userId, clientId }
 * @returns {Promise<[boolean, string?]>}
 */
export async function createSchedule(payload, session) {
    return apiTransaction({
        transType: 'schedule_add',
        payload: [payload],
        campusId: session.campusId,
        userId: session.userId,
        clientId: session.clientId,
    });
}

// ---------------------------------------------------------------------------
// Delete Schedules
// ---------------------------------------------------------------------------

/**
 * @param {Array<{report_id: number}>} items
 * @param {object} session — { campusId, userId, clientId }
 * @returns {Promise<[boolean, string?]>}
 */
/* /myrest -> mysql_transaction (docs/MYREST_MIGRATION_AUDIT.md section D): Super Admin+; only these columns, and only rows of the request's campus. */
export async function deleteSchedules(items, session) {
    const payload = items.map((item) => ({
        report_id: item.report_id,
        modified_by: session.userId,
    }));
    return apiTransaction({
        transType: 'schedule_delete',
        payload,
        campusId: session.campusId,
        clientId: session.clientId,
        userId: session.userId,
    });
}

// ---------------------------------------------------------------------------
// Send Schedule Report (immediate trigger)
// ---------------------------------------------------------------------------

/**
 * @param {object} schedule — the schedule row data
 * @param {number} campusId
 * @param {string} clientId
 * @returns {Promise<[boolean, string?]>}
 */
export async function sendScheduleReport(schedule, campusId, clientId) {
    const payload = {
        Content: [{
            campus_id: schedule.campus_id || campusId,
            description: schedule.description,
            source: 'portal',
            report_id: schedule.report_id,
            report_filter_terms: schedule.report_filter_terms,
            report_interval_id: schedule.report_interval_id,
            report_receiver: schedule.report_receiver,
            report_user_ids: schedule.report_user_ids || '',
            report_type_id: schedule.report_type_id,
            last_sent: schedule.last_sent,
            pdf_summary: schedule.pdf_summary,
            csv_details: schedule.csv_details,
            pdf_details: schedule.pdf_details,
        }],
        CampusId: campusId,
        ClientId: clientId,
    };

    try {
        // Fire-and-forget (nowait pattern from V1)
        fetch(getBaseUrl() + import.meta.env.VITE_SEND_REPORT, {
            method: 'POST',
            headers: await getHeaders(),
            body: JSON.stringify(payload),
        });
        return [true];
    } catch (err) {
        console.error('[scheduledApi] sendScheduleReport error:', err);
        return [false, 'Failed to send report.'];
    }
}

/** The full filter option set (utils/reportFilterOptions.js), with the synthetic "No Reason"
    row: a schedule created from a report page can filter on reason 0. The Environmental
    section hides it (excludeNoReason), matching its report page. */
export { fetchFilterOptions as fetchScheduleFilterOptions } from '../../../../utils/reportFilterOptions.js';
